import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import usePermissions from '../common/usePermissions';

const ProtectedRoute = ({ element, permissionKey, denyMessage, allowedActors }) => {
  const { isAuthenticated, isLoggedInCheck, user } = useAuth();
  const navigate = useNavigate();
  const { loading, hasPermission } = usePermissions();

  // If we've completed the login check and the user is not authenticated,
  // redirect to the login page. Use `isAuthenticated` (boolean) rather
  // than checking `user` directly for clearer intent.
  if (isLoggedInCheck && !isAuthenticated) {
    console.log('ProtectedRoute: redirecting to login');
    return <Navigate to="/login" />;
  }

  // Restrict route to specific actor types (staff, driver, owner)
  if (isAuthenticated && allowedActors && user) {
    const actorType = user.actor_type || 'staff';
    if (!allowedActors.includes(actorType)) {
      if (actorType === 'driver') return <Navigate to="/driver/dashboard" />;
      if (actorType === 'owner') return <Navigate to="/owner/dashboard" />;
      return <Navigate to="/" />;
    }
  }

  if (permissionKey) {
    if (loading) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] flex items-center justify-center px-4">
          <div className="rounded-xl border border-gray-200 bg-white px-6 py-5 text-sm text-gray-600 shadow-sm">
            Checking access...
          </div>
        </div>
      );
    }

    if (!hasPermission(permissionKey)) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] flex items-center justify-center px-4">
          <div className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h1 className="text-2xl font-bold text-[#2a2e30]">Access denied</h1>
            <p className="mt-2 text-sm text-gray-600">
              {denyMessage || 'You do not have permission to view this page.'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-6 inline-flex items-center rounded-sm bg-[#FF5733] px-4 py-2 text-sm font-semibold text-white hover:bg-[#e64520]"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      );
    }
  }

  return element;
};

export default ProtectedRoute;
